import toDoListRepository from "../repositories/toDoListRepository";
import repeatingEventByDateRepository from "../repositories/repeatingEventByDateRepository";
import moment from "moment";
import tasksHelper from "./tasksHelper";
const { newTodoId, nowIso, ensureTodoIds } = require("../sync/todoMerge.js");
const { daysToMaterialize } = require("./repeatingEventDays.js");

const MARK_GENERATED = "generated";
const MARK_SUPPRESSED = "suppressed";

function ensureReByDate(vue, listId) {
  let reByDate = vue.$store.getters.repeatingEventByDate[listId];
  if (!reByDate || typeof reByDate !== "object") {
    reByDate = {};
    vue.$store.commit("loadRepeatingEventGeneratedByDate", { key: listId, val: reByDate });
  }
  return reByDate;
}

function ensureTodoList(vue, listId) {
  let list = vue.$store.getters.todoLists[listId];
  if (!Array.isArray(list)) {
    vue.$store.commit("loadTodoLists", { todoListId: listId, todoList: [] });
    list = vue.$store.getters.todoLists[listId];
  }
  return list;
}

/** Keep at most one generated instance per repeatingEvent id in a day list. */
function dedupeRepeatingTodos(list) {
  if (!Array.isArray(list) || !list.length) {
    return { list: list || [], changed: false };
  }
  const seen = new Set();
  const next = [];
  let changed = false;
  for (const todo of list) {
    const reId = todo && todo.repeatingEvent;
    if (reId) {
      if (seen.has(reId)) {
        changed = true;
        continue;
      }
      seen.add(reId);
    }
    next.push(todo);
  }
  return { list: next, changed };
}

function persistReByDate(vue, listId, reByDate) {
  vue.$store.commit("loadRepeatingEventGeneratedByDate", { key: listId, val: reByDate });
  return repeatingEventByDateRepository.update(listId, reByDate);
}

function isSuppressed(mark) {
  return mark === MARK_SUPPRESSED || mark === true;
}

/**
 * After deleting a generated instance / clearing a day, suppress rematerialize.
 */
function markDayHandled(vue, listId, reId) {
  if (!vue || !listId || !reId) return;
  const reByDate = ensureReByDate(vue, listId);
  reByDate[reId] = MARK_SUPPRESSED;
  persistReByDate(vue, listId, reByDate);
}

async function generateRepeatingEventsIntances(listId, vue, options = {}) {
  // Off-screen days (next week) often have a stale weekCache revision from an
  // earlier visit — hydrate so materialize PUT does not 409 and drop instances.
  if (options.hydrate && vue && vue.$store) {
    try {
      const { hydrateTodoListDay } = require("../sync/sessionSync.js");
      await hydrateTodoListDay(vue.$store, listId);
    } catch (_) {
      /* offline / network: fall back to in-memory list */
    }
  }

  const r_events = vue.$store.getters.repeatingEventDateCache[listId] || [];
  if (!r_events.length) {
    const list = vue.$store.getters.todoLists[listId];
    if (Array.isArray(list)) {
      const { list: cleaned, changed } = dedupeRepeatingTodos(list);
      if (changed) {
        vue.$store.commit("loadTodoLists", { todoListId: listId, todoList: cleaned });
        return toDoListRepository.update(listId, cleaned);
      }
    }
    return;
  }

  let list = ensureTodoList(vue, listId);
  const idHeal = ensureTodoIds(list);
  if (idHeal.changed) {
    vue.$store.commit("loadTodoLists", { todoListId: listId, todoList: idHeal.list });
    list = idHeal.list;
  }
  const { list: cleaned, changed: deduped } = dedupeRepeatingTodos(list);
  if (deduped) {
    vue.$store.commit("loadTodoLists", { todoListId: listId, todoList: cleaned });
    list = cleaned;
  }

  const re_by_date = ensureReByDate(vue, listId);
  let listChanged = idHeal.changed || deduped;
  let markersChanged = false;

  list.forEach((todo) => {
    const reId = todo && todo.repeatingEvent;
    if (reId && !re_by_date[reId]) {
      re_by_date[reId] = MARK_GENERATED;
      markersChanged = true;
    }
  });

  r_events.forEach((re_id) => {
    if (!re_id) return;
    const mark = re_by_date[re_id];
    if (isSuppressed(mark)) return;

    const re = vue.$store.getters.repeatingEventList[re_id];
    if (!re || !re.data) {
      // Missing rule — suppress so we do not spin
      re_by_date[re_id] = MARK_SUPPRESSED;
      markersChanged = true;
      return;
    }

    const already = list.some((todo) => todo && todo.repeatingEvent === re_id);
    if (already) {
      if (mark !== MARK_GENERATED) {
        re_by_date[re_id] = MARK_GENERATED;
        markersChanged = true;
      }
      return;
    }

    // No instance: create (also heals orphan "generated" markers after a sync wipe)
    const new_instanced_event = JSON.parse(JSON.stringify(re.data));
    new_instanced_event.listId = listId;
    new_instanced_event.id = newTodoId();
    new_instanced_event.updatedAt = nowIso();
    new_instanced_event.repeatingEvent = re_id;
    vue.$store.commit("addTodo", new_instanced_event);
    list = vue.$store.getters.todoLists[listId];
    tasksHelper.applyListOrder(list, vue.$store.getters.config);
    listChanged = true;

    re_by_date[re_id] = MARK_GENERATED;
    markersChanged = true;
  });

  const writes = [];
  if (listChanged) {
    writes.push(toDoListRepository.update(listId, list));
  }
  if (markersChanged) {
    writes.push(Promise.resolve(persistReByDate(vue, listId, re_by_date)));
  }
  return Promise.all(writes.filter(Boolean));
}

function cascadeRemovedInstanceNotes(list, todoIds) {
  if (!todoIds || !todoIds.length) return;
  let cascadeDeleteTodoNotes;
  let deleteNotesByTodoId;
  let shouldCascadeTodoNotes;
  try {
    ({ cascadeDeleteTodoNotes, shouldCascadeTodoNotes } = require("./todoActivityLog.js"));
    ({ deleteNotesByTodoId } = require("../sync/engine"));
  } catch (_) {
    return;
  }
  todoIds.forEach((todoId) => {
    if (!shouldCascadeTodoNotes(list, todoId)) return;
    const pending = cascadeDeleteTodoNotes({ todoId, deleteFn: deleteNotesByTodoId });
    if (pending && typeof pending.then === "function") pending.then(() => {}, () => {});
  });
}

function removeGeneratedRepeatingEvents(listId, vue) {
  const dayList = vue.$store.getters.todoLists[listId];
  if (!Array.isArray(dayList)) return Promise.resolve();
  let changed = false;
  const removedIds = [];
  for (let index = dayList.length - 1; index >= 0; index--) {
    const todo = dayList[index];
    if (todo.repeatingEvent && !vue.$store.getters.repeatingEventList[todo.repeatingEvent]) {
      if (moment(todo.listId, "YYYYMMDD").isBefore(moment(), "day")) {
        todo.repeatingEvent = null;
      } else {
        if (todo.id) removedIds.push(todo.id);
        vue.$store.commit("removeTodo", {
          toDoListId: todo.listId,
          index: index,
        });
      }
      changed = true;
    }
  }
  if (changed) {
    const write = toDoListRepository.update(listId, vue.$store.getters.todoLists[listId]);
    return Promise.resolve(write).then((result) => {
      let ok = false;
      try {
        ok = require("./todoActivityLog.js").isTodoDeleteWriteOk(result);
      } catch (_) {
        ok = false;
      }
      if (ok) cascadeRemovedInstanceNotes(vue.$store.getters.todoLists[listId], removedIds);
      return result;
    });
  }
  return Promise.resolve();
}

export default {
  dedupeRepeatingTodos,
  daysToMaterialize,
  markDayHandled,
  MARK_GENERATED,
  MARK_SUPPRESSED,
  generateRepeatingEventsIntances,
  removeGeneratedRepeatingEvents,
};
